import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';

import { Modal } from '@/core/components/Modal';
import { Button } from '@/core/components/Button';
import { Icon } from '@/core/components/Icons';
import { Heading, Paragraph } from '@/core/components/Typography';
import { HEADER_HEIGHT } from '@/core/components/Layout/Layout.styles';
import { ButtonSize, ButtonVariant } from '@/core/components/Button/Button.types';
import { useTopmostZIndex } from '@/core/hooks/useTopmostZIndex';
import { RootState } from '@/core/interfaces/store';
import { useAppDispatch } from '@/core/store/store';

import {
  fetchTokenUsage,
  isAnalysisErrorForIdSelector,
  isAnalysisLoadingForIdSelector,
  selectedAnalysisSelector,
  tokenUsagePercentageSelector,
  tokenUsageSelector,
} from '@/features/ChatBot/store';
import { TokenUsageBar } from '@/features/ChatBot/components/TokenUsageBar';
import {
  downloadAnalysisPdf,
  getPdfFileNameFromTitle,
} from '@/features/ChatBot/utils/exportAnalysisPdf';

import {
  CloseButton,
  ContentInner,
  HeaderActions,
  LocationLabel,
  ModalContent,
  PublicationDate,
  TitleRow,
  TitleWrapper,
  ViewBody,
  ViewHeader,
} from './AnalysisViewModal.styles';
import { AnalysisMetadataContent } from './AnalysisMetadataContent';
import { AnalysisViewModalProps } from './AnalysisViewModel.types';

export const AnalysisViewModal = ({ isOpen, onClose, onEdit }: AnalysisViewModalProps) => {
  const dispatch = useAppDispatch();
  const { colors } = useTheme();
  const zIndex = useTopmostZIndex(isOpen);
  const [exportingPdf, setExportingPdf] = useState(false);

  const selectedAnalysis = useSelector(selectedAnalysisSelector);
  const tokenUsage = useSelector(tokenUsageSelector);
  const tokenUsagePercentage = useSelector(tokenUsagePercentageSelector);

  const analysisId = selectedAnalysis?.id ?? null;
  const titleProp = selectedAnalysis?.title ?? 'Analysis';
  const publicationDate = selectedAnalysis?.publicationDatetime
    ? dayjs(selectedAnalysis.publicationDatetime).format('dddd D MMMM YYYY')
    : undefined;

  const loading = useSelector((state: RootState) =>
    isAnalysisLoadingForIdSelector(state, analysisId)
  );
  const error = useSelector((state: RootState) => isAnalysisErrorForIdSelector(state, analysisId));

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchTokenUsage());
    }
  }, [dispatch, isOpen]);

  const handleExportPdf = useCallback(async () => {
    if (analysisId == null) return;

    setExportingPdf(true);

    try {
      const fileName = getPdfFileNameFromTitle(titleProp ?? titleProp);

      await downloadAnalysisPdf(dispatch, analysisId, fileName);
    } finally {
      setExportingPdf(false);
    }
  }, [dispatch, titleProp, analysisId]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      matchContentSize={false}
      height={`calc(100vh - ${HEADER_HEIGHT})`}
      zIndex={zIndex}
      positionX="flex-end"
      positionY="flex-start"
      clickThroughOverlay
      shouldCloseOnOverlayClick={false}
      overlayPosition={{
        top: HEADER_HEIGHT,
        right: '0',
        left: '0',
      }}
    >
      <ModalContent>
        <ContentInner>
          <ViewHeader>
            <TitleWrapper>
              <TitleRow>
                {typeof selectedAnalysis?.location === 'string' && (
                  <LocationLabel>{selectedAnalysis.location} | </LocationLabel>
                )}
                <Heading
                  type="h2"
                  color={colors.primaryText}
                >
                  {titleProp}
                </Heading>
              </TitleRow>
              {publicationDate && <PublicationDate>{publicationDate}</PublicationDate>}
            </TitleWrapper>
            <HeaderActions>
              <Button
                variant={ButtonVariant.PRIMARY}
                buttonSize={ButtonSize.SMALL}
                onClick={handleExportPdf}
                disabled={analysisId == null || exportingPdf}
                type="button"
              >
                {exportingPdf ? 'Exporting…' : 'Export'}
              </Button>
              {onEdit && (
                <Button
                  variant={ButtonVariant.TERTIARY}
                  buttonSize={ButtonSize.SMALL}
                  onClick={() => onEdit()}
                >
                  Edit
                </Button>
              )}
              <CloseButton
                type="button"
                onClick={onClose}
                aria-label="Close"
              >
                <Icon
                  name="Close"
                  size={14}
                  color={colors.primaryText}
                />
              </CloseButton>
            </HeaderActions>
          </ViewHeader>
          {tokenUsage && (
            <TokenUsageBar
              percentage={tokenUsagePercentage}
              usedTokens={tokenUsage.usedTokens}
              remainingTokens={tokenUsage.remainingTokens}
            />
          )}
          <ViewBody>
            {loading && (
              <Paragraph
                type="normal"
                color={colors.secondaryText}
              >
                Loading…
              </Paragraph>
            )}
            {error && (
              <Paragraph
                type="normal"
                color={colors.secondaryText}
              >
                Failed to load analysis.
              </Paragraph>
            )}
            {!loading && !error && selectedAnalysis && (
              <AnalysisMetadataContent analysis={selectedAnalysis} />
            )}
          </ViewBody>
        </ContentInner>
      </ModalContent>
    </Modal>
  );
};
